'use client'

import { useRef, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { motion, useInView } from 'framer-motion'
import { ArrowRight } from 'lucide-react'

const featuredProjects = [
  { slug: 'the-delhi-mall', name: 'TheDelhiMall.com', tag: 'Next.js + Three.js · Real Estate', color: '#1a1208', image: '/logos/thedelhimall.png' },
  { slug: 'revoraa', name: 'Revoraa.in', tag: 'Full Stack · Food E-Commerce', color: '#081210', image: '/logos/revoraa.png' },
  { slug: 'betterchoice-realtors', name: 'BetterChoiceRealtors.com', tag: 'Real Estate Developer · Next.js', color: '#121008', image: '/logos/betterchoicerealtors.png' },
  { slug: 'wedding-stories', name: 'Wedding Stories', tag: 'Photography Portfolio · React', color: '#0a0812', image: '/logos/weddstories.png' },
  { slug: 'onwork-ai', name: 'Onwork.ai', tag: 'AI Chat App · React + Node', color: '#080e18', image: '/logos/onworkai.png' },
  { slug: 'mintginger', name: 'Mintginger.in', tag: 'Channel Partner Platform · Next.js', color: '#0e1208', image: '/logos/mintginger.png' },
]

import { useMotionValue, useSpring, useTransform } from 'framer-motion'

function ProjectCard({ project, index }: { project: typeof featuredProjects[0]; index: number }) {
  const x = useMotionValue(0)
  const y = useMotionValue(0)

  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [10, -10]), { stiffness: 300, damping: 30 })
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-10, 10]), { stiffness: 300, damping: 30 })

  function handleMouseMove(event: React.MouseEvent<HTMLDivElement>) {
    const rect = event.currentTarget.getBoundingClientRect()
    const width = rect.width
    const height = rect.height
    const mouseXPos = event.clientX - rect.left
    const mouseYPos = event.clientY - rect.top

    const xPct = (mouseXPos / width) - 0.5
    const yPct = (mouseYPos / height) - 0.5

    x.set(xPct)
    y.set(yPct)
    mouseX.set(mouseXPos)
    mouseY.set(mouseYPos)
  }

  function handleMouseLeave() {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      className="relative group perspective-1000"
    >
      <motion.div
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d',
          background: `linear-gradient(135deg, ${project.color} 0%, #0F172A 100%)`,
        }}
        className="relative flex-shrink-0 w-[340px] md:w-[480px] aspect-[16/10] overflow-hidden cursor-pointer border border-white/10 hover:border-[#C8A96E]/40 transition-colors duration-500 rounded-xl"
      >
        {/* Dynamic Glow Effect */}
        <motion.div
          className="pointer-events-none absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0"
          style={{
            background: useTransform(
              [mouseX, mouseY],
              ([x, y]) => `radial-gradient(600px circle at ${x}px ${y}px, rgba(200, 169, 110, 0.15), transparent 40%)`
            ),
          }}
        />

        {/* Grid Overlay */}
        <div className="absolute inset-0 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity duration-500"
          style={{
            backgroundImage: 'linear-gradient(#C8A96E 1px, transparent 1px), linear-gradient(90deg, #C8A96E 1px, transparent 1px)',
            backgroundSize: '30px 30px',
          }}
        />

        {/* Project Logo - Floating with Z-offset */}
        {project.image && (
          <div 
            className="absolute inset-x-16 inset-y-20 flex items-center justify-center z-10 transition-all duration-700 ease-out group-hover:scale-110 group-hover:-translate-y-8"
            style={{ transform: 'translateZ(50px)' }}
          >
            <div className="relative w-full h-full">
              <Image 
                src={project.image} 
                alt={project.name} 
                fill 
                className="object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)] group-hover:drop-shadow-[0_40px_80px_rgba(200,169,110,0.3)] transition-all duration-500" 
              />
            </div>
          </div>
        )}

        {/* Info Section - Glassmorphism */}
        <div 
          className="absolute bottom-0 left-0 right-0 p-8 z-20 translate-z-[30px] group-hover:translate-y-4 transition-transform duration-500"
          style={{ transform: 'translateZ(30px)' }}
        >
          <div className="relative">
            <motion.span
              className="text-[#C8A96E] text-[10px] tracking-[0.3em] uppercase mb-3 block opacity-70 group-hover:opacity-100 transition-opacity"
              style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
            >
              {project.tag}
            </motion.span>
            <h3
              className="text-4xl text-[#F8FAFC] group-hover:text-white transition-colors"
              style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.04em' }}
            >
              {project.name}
            </h3>
          </div>
        </div>

        {/* View Project Button Revealed on Hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 z-30 translate-z-[60px] group-hover:translate-y-8" style={{ transform: 'translateZ(60px)' }}>
          <div className="bg-[#0F172A]/40 backdrop-blur-md border border-[#C8A96E]/30 p-1 rounded-full overflow-hidden scale-90 group-hover:scale-100 transition-transform duration-500">
            <Link
              href={`/work/${project.slug}`}
              className="flex items-center gap-3 text-[#C8A96E] text-xs font-bold tracking-[0.2em] px-8 py-4 bg-[#0F172A] hover:bg-[#C8A96E] hover:text-[#0F172A] transition-all duration-300 rounded-full"
              style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
            >
              EXPLORE CASE <span className="sr-only">for {project.name}</span> <ArrowRight size={14} strokeWidth={3} />
            </Link>
          </div>
        </div>

        {/* Edge Reflection */}
        <div className="absolute inset-0 border border-white/5 group-hover:border-white/10 transition-colors pointer-events-none rounded-xl" />
      </motion.div>
    </motion.div>
  )
}

export default function WorkTeaser() {
  return (
    <section className="py-32 bg-[#0F172A] overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-px bg-[#C8A96E]" />
            <span
              className="text-[#C8A96E] text-xs tracking-[0.3em]"
              style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
            >
              PORTFOLIO
            </span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <h2
                className="text-[clamp(3rem,8vw,7rem)] leading-none text-[#F8FAFC] block"
                style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.02em' }}
              >
                SELECTED
              </h2>
              <h2
                className="text-[clamp(3rem,8vw,7rem)] leading-none text-[#C8A96E] block"
                style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.02em' }}
              >
                WORK
              </h2>
            </div>
            <Link
              href="/work"
              className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.1em] mb-4"
              style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
            >
              VIEW ALL WORK <ArrowRight size={13} />
            </Link>
          </div>
          {/* Gold rule */}
          <div className="h-px bg-gradient-to-r from-[#C8A96E]/40 to-transparent mt-6" />
        </motion.div>
      </div>

      {/* Horizontal scroll carousel */}
      <div className="relative mt-8">
        <div 
          className="flex gap-8 px-[10vw] overflow-x-auto pb-20 no-scrollbar select-none"
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            maskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)',
            WebkitMaskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)'
          }}
        >
          {featuredProjects.map((project, index) => (
            <ProjectCard key={project.slug} project={project} index={index} />
          ))}
          {/* Spacer for end fade */}
          <div className="flex-shrink-0 w-[10vw]" />
        </div>
      </div>
    </section>
  )
}
